import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./common-view.css";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { clearAuthSession } from "../utils/auth";

const CLIENT_PAGE_SIZE = 5;

export default function ClientSearch({ onSelect }) {
  const { t } = useTranslation();
  const auth = useAuthSession();
  const { token, isAdmin } = auth;
  const [selectedClient, setSelectedClient] = useState(null);

  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientPage, setClientPage] = useState(0);

  useEffect(() => {
    if (!token) {
      window.location.href = "/login";
      return;
    }

    setErr("");

    (async () => {
      try {
        const res = await fetch(`${BACKEND_BASE_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          const msg =
            res.status === 401 || res.status === 403
              ? t("commonLabel.invalidOrExpiredToken", "Invalid or expired token")
              : json.error || t("commonLabel.failedLoadUser", "Failed to load user");

          if (res.status === 401 || res.status === 403) {
            clearAuthSession();
          }

          throw new Error(msg);
        }
      } catch (e) {
        setErr(e.message || t("commonLabel.invalidOrExpiredToken", "Invalid or expired token"));
      }
    })();
  }, [token, onSelect, t]);

  async function loadClients(search = "") {
    if (!isAdmin) return;

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/api/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t("commonLabel.failedLoadingClients", "Failed loading clients"));

      setClients(Array.isArray(json.clients) ? json.clients : []);
      setClientPage(0);
    } catch (e) {
      console.error(e);
      alert(e.message || t("commonLabel.failedLoadingClients", "Failed loading clients"));
    } finally {
      setLoading(false);
    }
  }

  function pickClient(c) {
    setSelectedClient(c);
    onSelect?.(c);
  }

  const paginatedClients = clients.slice(
    clientPage * CLIENT_PAGE_SIZE,
    (clientPage + 1) * CLIENT_PAGE_SIZE
  );

  const hasNextPage = (clientPage + 1) * CLIENT_PAGE_SIZE < clients.length;
  const hasPrevPage = clientPage > 0;

  if (err) {
    return (
      <div className="view-page">
        <div className="view-card">
          <h1 className="view-title">{t("commonLabel.title")}</h1>
          <p className="view-error">❌ {err}</p>
          <div className="view-actions">
            <a className="btn-primary" href="/login">
              {t("commonLabel.login")}
            </a>
            <a className="btn-secondary" href="/">
              {t("commonLabel.home")}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-tools">
      <div className="admin-tools-title">
        {t("commonLabel.searchClient")}
      </div>

      <div className="client-select">
        <div className="view-label">
          {t("commonLabel.selectClient")}
        </div>

        <div className="selected-client">
          <strong>{t("commonLabel.selected")}: </strong>{" "}
          {selectedClient
            ? `${selectedClient.business_name}`
            : "—"}
        </div>

        {isAdmin && (
          <>
            <div className="client-search-row">
              <input
                className="client-search-input"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder={t("commonLabel.searchPlaceholder")}
              />

              <button
                className="btn-secondary"
                onClick={() => loadClients(q)}
                disabled={loading}
              >
                {loading
                  ? t("commonLabel.loading")
                  : t("commonLabel.search")}
              </button>
            </div>

            <div className="card">
              <div className="card-title">
                {t("commonLabel.clients", { count: clients.length })}
              </div>

              <div className="client-list">
                {paginatedClients.map(c => (
                  <button
                    key={c.client_id}
                    className={
                      "client-item" +
                      (selectedClient?.client_id === c.client_id
                        ? " selected"
                        : "")
                    }
                    onClick={() => pickClient(c)}
                  >
                    <div style={{ fontWeight: 800 }}>
                      {c.business_name || t("commonLabel.unnamedClient", "(Unnamed client)")}
                    </div>

                    <div className="muted small">
                      {t("commonLabel.plan")} {c.tier_plan || "none"} ·{" "}
                      {t("commonLabel.status")}{" "}
                      {c.active_ends_at
                        ? t("commonLabel.activeUntil", {
                            date: String(c.active_ends_at).slice(0, 10)
                          })
                        : t("commonLabel.inactive")}
                    </div>
                  </button>
                ))}
              </div>

              {clients.length > CLIENT_PAGE_SIZE && (
                <div className="common-pagination">
                  {hasPrevPage && (
                    <button
                      className="btn-link"
                      onClick={() => setClientPage(p => p - 1)}
                    >
                      {t("commonLabel.previous")}
                    </button>
                  )}

                  {hasNextPage && (
                    <button
                      className="btn-link"
                      onClick={() => setClientPage(p => p + 1)}
                    >
                      {t("commonLabel.next")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}